import {
  Shield,
  UserPlus,
  Search,
  Check,
  Loader2,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, toast } from '@aaas/shared';
import { ATSRole, ATS_ROLE_LABELS, ATS_ROLE_COLORS } from '../../types/member-role';
import {
  useMemberRoleListQuery,
  useUpdateMemberRoleMutation,
} from '../../hooks/useMemberRoleQueries';

const ROLE_ORDER: ATSRole[] = [ATSRole.RECRUITER, ATSRole.LEADER, ATSRole.MANAGER];

export function RolesPage() {
  const { t } = useTranslation('ats');
  const { idToken, currentWorkspace } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const getRoleLabel = (role: ATSRole) => t(`roles.labels.${role}`, ATS_ROLE_LABELS[role]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: memberRolesData, isLoading: rolesLoading } = useMemberRoleListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
  });

  const existingRoles = memberRolesData?.roles || [];
  const isManager = memberRolesData?.currentUserRole === 'manager';

  const filteredExisting = useMemo(() => {
    if (!debouncedSearch) return existingRoles;
    const q = debouncedSearch.toLowerCase();
    return existingRoles.filter(
      (m) =>
        m.account_name.toLowerCase().includes(q) ||
        m.account_email.toLowerCase().includes(q)
    );
  }, [existingRoles, debouncedSearch]);

  const updateMutation = useUpdateMemberRoleMutation({
    workspaceId: currentWorkspace?.id || '',
    idToken: idToken || '',
    onSuccess: () => {
      setUpdatingId(null);
      toast.success(t('roles.toast.updated'));
    },
    onError: (error) => {
      setUpdatingId(null);
      toast.error(error.message || t('roles.toast.updateFailed'));
    },
  });

  const handleExistingRoleClick = (accountId: string, role: ATSRole) => {
    setUpdatingId(accountId);
    updateMutation.mutate({ accountId, data: { role } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-gray-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('roles.pageTitle')}</h1>
          <p className="text-sm text-gray-500">{t('roles.pageDescription')}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">{t('roles.membersTitle')}</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {isManager
              ? t('roles.managerHint')
              : t('roles.viewerHint')}
          </p>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('roles.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {rolesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">{t('roles.loading')}</span>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">{t('roles.member')}</th>
                    {ROLE_ORDER.map((role) => (
                      <th key={role} className="px-4 py-2.5 text-center font-medium">
                        <span className={ATS_ROLE_COLORS[role].text}>{getRoleLabel(role)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {filteredExisting.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                        {t('roles.empty')}
                      </td>
                    </tr>
                  ) : (
                    filteredExisting.map((member) => {
                      const isUpdating = updatingId === member.account_id;
                      const canEdit = isManager && member.can_modify;
                      return (
                        <tr key={member.account_id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <p className="font-medium text-gray-900">{member.account_name}</p>
                            <p className="text-xs text-gray-500">{member.account_email}</p>
                          </td>
                          {ROLE_ORDER.map((role) => {
                            const isActive = member.role === role;
                            const colors = ATS_ROLE_COLORS[role];
                            return (
                              <td key={role} className="px-4 py-2.5 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    canEdit && !isUpdating && !isActive
                                      ? handleExistingRoleClick(member.account_id, role)
                                      : undefined
                                  }
                                  disabled={!canEdit || isUpdating || isActive}
                                  className={[
                                    'inline-flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all',
                                    isActive
                                      ? `${colors.bg} border-current ${colors.text}`
                                      : 'border-gray-200 bg-white',
                                    canEdit && !isUpdating && !isActive
                                      ? 'hover:border-gray-400 cursor-pointer'
                                      : 'cursor-default opacity-70',
                                  ].join(' ')}
                                  title={
                                    canEdit
                                      ? t('roles.setRole', { role: getRoleLabel(role) })
                                      : member.is_owner
                                        ? t('roles.workspaceOwner')
                                        : getRoleLabel(role)
                                  }
                                >
                                  {isUpdating ? (
                                    isActive ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : null
                                  ) : isActive ? (
                                    <Check className={`h-4 w-4 ${colors.text}`} />
                                  ) : null}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
